// ErrorBoundary.js
//
// Filet de securite racine : sans lui, la moindre exception levee pendant un
// rendu React demonte tout l'arbre et laisse une PAGE BLANCHE, sans message ni
// recours. En classe, cela se traduit par « ca marche plus » pour une salle
// entiere.
//
// Il traite deux situations distinctes :
//
// 1. CHUNK OBSOLETE (le cas frequent, et le seul qui se repare tout seul).
//    Les routes sont chargees en `lazy()` et `deploy.py` fait un
//    `rsync --delete` : apres un deploiement, les anciens chunks hashes
//    n'existent PLUS sur le serveur. Un utilisateur dont l'onglet est reste
//    ouvert demande un fichier disparu des qu'il navigue -> ChunkLoadError.
//    `useVersionCheck` ne le previent pas dans ce cas : il ne reagit qu'a un
//    changement major/minor, alors que les hashs changent a CHAQUE build.
//    La reponse correcte est de recharger : le HTML rejoue pointe vers les
//    nouveaux chunks et l'utilisateur ne voit qu'un clignotement.
//
// 2. TOUTE AUTRE ERREUR : un rechargement n'y changerait rien, on affiche un
//    ecran lisible avec une action explicite plutot qu'une page blanche.
//
// Le style est volontairement EN LIGNE. Un ErrorBoundary se declenche
// justement quand le chargement a mal tourne ; s'il dependait d'un fichier CSS
// qui peut lui aussi manquer, l'ecran de secours serait illisible.

import React from 'react';
import { reportClientError } from '../utils/reportClientError';
import { isChunkLoadError } from '../utils/chunkObsolete';
import i18n from '../i18n';

// Fenetre pendant laquelle on considere qu'un rechargement vient d'avoir lieu.
// Sert d'anti-boucle : si l'erreur persiste juste apres un rechargement, c'est
// que le probleme n'etait pas un chunk obsolete, et on cesse de recharger.
// Au-dela de ce delai, la reprise automatique redevient possible : un
// utilisateur qui garde l'onglet ouvert toute la journee traverse plusieurs
// deploiements, et chacun doit pouvoir etre rattrape.
const RELOAD_GUARD_MS = 20000;
const RELOAD_GUARD_KEY = 'edubot_chunk_reload_at';

/**
 * sessionStorage peut lever (mode privé Safari, stockage sature, iframe
 * cloisonnee). Le chatbot etant justement diffusable en iframe, on ne laisse
 * jamais une erreur de stockage empecher la reprise.
 */
function readReloadGuard() {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_GUARD_KEY);
    return raw ? parseInt(raw, 10) : 0;
  } catch (e) {
    return 0;
  }
}

function writeReloadGuard(value) {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(value));
  } catch (e) {
    /* sans stockage, on accepte le risque d'un second rechargement */
  }
}

// La remontee vit dans utils/reportClientError.js : elle est partagee avec les
// gestionnaires globaux (promesses rejetees, erreurs hors React), qui couvrent
// tout ce qui n'est PAS une exception de rendu — et qui echappait donc a cet
// ErrorBoundary.

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isReloading: false };
  }

  static getDerivedStateFromError(error) {
    // Un chunk obsolete se repare par un rechargement, a condition qu'on n'en
    // sorte pas d'un : on decide ici pour afficher le bon ecran du premier coup
    // (« Mise a jour... » plutot qu'un message d'erreur qui disparait aussitot).
    const now = Date.now();
    const canReload =
      isChunkLoadError(error) && now - readReloadGuard() > RELOAD_GUARD_MS;

    return { hasError: true, isReloading: canReload };
  }

  componentDidCatch(error, errorInfo) {
    // console.error reste actif en production (cf. consoleWrapper.js) : c'est
    // la seule trace exploitable quand un utilisateur signale un incident.
    console.error('[ErrorBoundary] Erreur non rattrapee :', error, errorInfo);

    // ...et on ne compte plus sur le fait qu'il le signale.
    // `reportClientError` rend une promesse qui ne rejette jamais. Pour un chunk
    // obsolete, elle compare d'abord l'onglet au `index.html` du serveur (un
    // aller-retour) : le rechargement l'ATTEND, sans quoi il couperait cette
    // verification et un vrai defaut ne serait plus rapporte.
    const rapport = reportClientError(error);

    if (this.state.isReloading) {
      writeReloadGuard(Date.now());
      // Les caches peuvent servir l'ancien index.html, qui redemanderait les
      // memes chunks disparus : on les vide avant de recharger. Le rechargement
      // a lieu dans tous les cas, meme si la purge echoue.
      const reload = () => window.location.reload();
      const purge = () => {
        if (!('caches' in window)) return undefined;
        return window.caches.keys()
          .then((names) => Promise.all(names.map((n) => window.caches.delete(n))));
      };
      Promise.resolve(rapport).then(purge).then(reload, reload);
    }
  }

  handleReload = () => {
    writeReloadGuard(Date.now());
    window.location.reload();
  };

  handleGoHome = () => {
    writeReloadGuard(Date.now());
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const containerStyle = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '20px',
      backgroundColor: 'var(--app-background, #ffffff)',
      color: 'var(--text-primary, #1a1a1a)',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      textAlign: 'center',
    };

    // Rechargement en cours : on annonce une mise a jour, pas une panne. C'est
    // la verite technique (le code a change) et cela evite d'inquieter un eleve
    // pour un ecran qui va disparaitre en une seconde.
    if (this.state.isReloading) {
      return (
        <div style={containerStyle}>
          <div>
            <div
              style={{
                border: '4px solid var(--border-color, #e0e0e0)',
                borderTop: '4px solid var(--primary, #0066cc)',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                animation: 'ebspin 1s linear infinite',
                margin: '0 auto 20px',
              }}
            />
            <p style={{ fontSize: '15px', margin: 0 }}>
              {i18n.t('app:ErrorBoundary.miseJourDeApplication')}
            </p>
            <style>{i18n.t('app:ErrorBoundary.keyframesEbspinTransformRotate0deg')}</style>
          </div>
        </div>
      );
    }

    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: '460px' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }} aria-hidden="true">
            ⚠️
          </div>
          <h1 style={{ fontSize: '20px', margin: '0 0 12px', fontWeight: 600 }}>
            {i18n.t('app:App.uneErreurEstSurvenue')}
          </h1>
          <p
            style={{
              fontSize: '14px',
              lineHeight: 1.6,
              margin: '0 0 24px',
              color: 'var(--text-secondary, #666666)',
            }}
          >
            {i18n.t('app:ErrorBoundary.affichageDeCettePageEte')}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={this.handleReload}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: 'var(--primary, #0066cc)',
                color: '#ffffff',
              }}
            >
              {i18n.t('app:ErrorBoundary.rechargerLaPage')}
            </button>
            <button
              onClick={this.handleGoHome}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                color: 'var(--text-primary, #1a1a1a)',
                border: '1px solid var(--border-color, #e0e0e0)',
              }}
            >
              {i18n.t('app:PreviewBot.retourAccueil')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
